import React, { useMemo } from 'react';
import { Box, Grid2 as Grid, Stack, SxProps, Theme } from '@mui/material';
import FollowUpSection from '../sections/FollowUpSection';
import IssuesSection from '../sections/IssuesSection';
import PeopleSection from '../sections/PeopleSection';
import HealthcheckSection from '../sections/HealthcheckSection';
import { EnhancedDashboardItemData, DashboardSectionConfig, DomainDashboardDataGetters } from '../../types';
import OptionsSection from '../sections/OptionsSection';
import {
  createMemoizedGetter,
  DashboardItemData,
  DashboardMenuOption,
  useDashboardContext,
} from 'framework/ui/dashboard';

interface DashboardItemWithSectionsProps {
  item: EnhancedDashboardItemData | DashboardItemData;
  sectionsConfig: DashboardSectionConfig;
  menuOptions?: DashboardMenuOption[];
  onFollowUpRemove?: (itemId: string | number, followUpId: string | number) => void;
  onFollowUpUpdate?: (
    itemId: string | number,
    followUpId: string | number,
    data: { description: string; date: string },
  ) => void;
  sxSectionsWrapper?: SxProps<Theme>;
  healthcheckStatusConfig?: Record<string, { textColor: string; buttonColor: string }>;
  healthcheckGetStatusByMonths?: (months: number) => string;
}

const DashboardItemWithSections: React.FC<DashboardItemWithSectionsProps> = ({
  item,
  sectionsConfig,
  menuOptions,
  onFollowUpRemove,
  onFollowUpUpdate,
  sxSectionsWrapper,
  healthcheckStatusConfig,
  healthcheckGetStatusByMonths,
}) => {
  const { dataGetters } = useDashboardContext<DomainDashboardDataGetters>();

  const memoizedItemProcessor = useMemo(
    () =>
      createMemoizedGetter<{ item: DashboardItemData; dataGetters?: typeof dataGetters }, EnhancedDashboardItemData>({
        key: 'processItemWithSections',
        getter: ({ item: itemData, dataGetters: getters }) => {
          if ('sections' in itemData && itemData.sections) {
            return itemData as EnhancedDashboardItemData;
          }

          if (getters) {
            return {
              ...itemData,
              sections: {
                followUps: getters.followUps?.(itemData) || [],
                issues: getters.issues?.(itemData) || [],
                people: getters.people?.(itemData) || [],
                healthcheck: getters.healthcheck?.(itemData),
              },
            };
          }

          return itemData as EnhancedDashboardItemData;
        },
        dependencies: ['item', 'dataGetters'],
      }),
    [],
  );

  const enhancedItem = memoizedItemProcessor({ item, dataGetters });

  return (
    <Box>
      <Grid container spacing={3} alignItems="flex-start">
        <Grid size="grow">
          <Stack
            direction="row"
            spacing={3}
            alignItems="flex-start"
            flexWrap="nowrap"
            justifyContent="space-between"
            pt={0.5}
            sx={sxSectionsWrapper}
          >
            {sectionsConfig.followUps?.enabled && (
              <FollowUpSection
                followUps={enhancedItem.sections?.followUps || []}
                maxItems={sectionsConfig.followUps.maxItems}
                title={sectionsConfig.followUps.title}
                onRemove={onFollowUpRemove ? followUpId => onFollowUpRemove(item.id, followUpId) : undefined}
                onUpdate={
                  onFollowUpUpdate ? (followUpId, data) => onFollowUpUpdate(item.id, followUpId, data) : undefined
                }
              />
            )}

            {sectionsConfig.issues?.enabled && (
              <IssuesSection
                issues={enhancedItem.sections?.issues || []}
                maxItems={sectionsConfig.issues.maxItems}
                showSeverityColors={sectionsConfig.issues.showSeverityColors}
                title={sectionsConfig.issues.title}
              />
            )}

            {sectionsConfig.healthcheck?.enabled && (
              <HealthcheckSection
                healthcheck={enhancedItem.sections?.healthcheck}
                config={sectionsConfig.healthcheck}
                title={sectionsConfig.healthcheck.title}
                statusConfig={healthcheckStatusConfig}
                getStatusByMonths={healthcheckGetStatusByMonths}
              />
            )}

            {sectionsConfig.people?.enabled && (
              <PeopleSection
                people={enhancedItem.sections?.people || []}
                maxItems={sectionsConfig.people.maxItems}
                showPosition={sectionsConfig.people.showPosition}
                title={sectionsConfig.people.title}
                isOpenInNewTab={sectionsConfig.people.isOpenInNewTab}
              />
            )}
            {menuOptions && menuOptions.length > 0 && <OptionsSection options={menuOptions} data={item} />}
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DashboardItemWithSections;
